import { useState } from 'react';

interface ServiceCTABlockProps {
  servicePreset?: string;
  headline?: string;
  subtext?: string;
}

const FORM_ACTION = 'https://readdy.ai/api/form/d72jcgcij602egd95je0';

const ServiceCTABlock = ({
  servicePreset = '',
  headline = 'Ready to Get Started?',
  subtext = "Get a free, no-obligation estimate. We respond fast, communicate clearly, and deliver exceptional electrical work every time.",
}: ServiceCTABlockProps) => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const desc = (form.querySelector('[name="description"]') as HTMLTextAreaElement)?.value || '';
    if (desc.length > 500) return;

    setStatus('sending');
    const formData = new URLSearchParams();
    new FormData(form).forEach((val, key) => formData.append(key, val as string));

    try {
      const res = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="estimate" className="relative bg-dp py-28 overflow-hidden">
      {/* BG glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-[#00D9FF]/5 blur-[120px] pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00D9FF]/30 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left — text */}
          <div className="lg:pt-4 scroll-reveal scroll-from-left">
            <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-5">
              Let&apos;s Talk
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[var(--tp)] leading-tight mb-6">
              {headline.split(' ').slice(0, -1).join(' ')}{' '}
              <span className="text-[#00D9FF] [text-shadow:0_0_36px_rgba(0,217,255,0.4)]">
                {headline.split(' ').slice(-1)}
              </span>
            </h2>
            <p className="text-[var(--td)] text-[16px] leading-relaxed mb-10 max-w-md">{subtext}</p>

            {/* Trust chips */}
            <div className="flex flex-col gap-3.5">
              {[
                { icon: 'ri-shield-check-fill', text: 'Licensed & fully insured in Michigan' },
                { icon: 'ri-time-fill', text: 'Fast response — typically within 1 business day' },
                { icon: 'ri-medal-fill', text: 'No pressure, no upsells — just honest advice' },
                { icon: 'ri-phone-fill', text: 'Or call us directly: (269) 370-0765' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#00D9FF]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#00D9FF] text-sm`} />
                  </span>
                  <span className="text-[var(--td)] text-[14px]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 scroll-reveal scroll-from-right">
            <h3 className="text-xl font-black text-white mb-1">Request a Free Estimate</h3>
            <p className="text-[#7A9AB8] text-[13px] mb-6">No obligation. We&apos;ll get back to you promptly.</p>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00D9FF]/15 border border-[#00D9FF]/30">
                  <i className="ri-check-double-line text-2xl text-[#00D9FF]" />
                </div>
                <p className="text-white font-bold text-lg">Request Sent!</p>
                <p className="text-[#5E7A96] text-[14px] max-w-xs">Thank you — we&apos;ll be in touch within 1 business day.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id={`service-estimate-${servicePreset.replace(/\s+/g, '-').toLowerCase()}`}
                action={FORM_ACTION}
                method="POST"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-[#7A9AB8] uppercase tracking-wider mb-1.5">Full Name *</label>
                    <input type="text" name="full_name" required placeholder="John Smith"
                      className="w-full bg-white/5 border border-white/10 focus:border-[#00D9FF]/50 focus:ring-1 focus:ring-[#00D9FF]/20 text-white placeholder-white/20 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-[#7A9AB8] uppercase tracking-wider mb-1.5">Phone *</label>
                    <input type="tel" name="phone" required placeholder="(269) 555-0100"
                      className="w-full bg-white/5 border border-white/10 focus:border-[#00D9FF]/50 focus:ring-1 focus:ring-[#00D9FF]/20 text-white placeholder-white/20 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#7A9AB8] uppercase tracking-wider mb-1.5">Email</label>
                  <input type="email" name="email" placeholder="john@email.com"
                    className="w-full bg-white/5 border border-white/10 focus:border-[#00D9FF]/50 focus:ring-1 focus:ring-[#00D9FF]/20 text-white placeholder-white/20 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200" />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#7A9AB8] uppercase tracking-wider mb-1.5">Service Needed *</label>
                  <select name="service" required defaultValue={servicePreset}
                    className="w-full bg-[#0A1628] border border-white/10 focus:border-[#00D9FF]/50 text-white rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200 cursor-pointer">
                    <option value="">Select a service...</option>
                    <option value="New Construction Wiring">New Construction Wiring</option>
                    <option value="Residential Service & Repair">Residential Service &amp; Repair</option>
                    <option value="Generator Installation">Generator Installation</option>
                    <option value="EV Charger Installation">EV Charger Installation</option>
                    <option value="Solar & Panel Upgrade">Solar &amp; Panel Upgrade</option>
                    <option value="Specialty Lighting">Specialty Lighting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#7A9AB8] uppercase tracking-wider mb-1.5">
                    Project Details
                    <span className="ml-2 text-[#3A566E] normal-case font-normal">{charCount}/500</span>
                  </label>
                  <textarea name="description" rows={3} maxLength={500}
                    placeholder="Tell us about your project..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/5 border border-white/10 focus:border-[#00D9FF]/50 focus:ring-1 focus:ring-[#00D9FF]/20 text-white placeholder-white/20 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200 resize-none" />
                </div>

                {status === 'error' && (
                  <p className="text-red-400 text-[13px]">Something went wrong. Please call us at (269) 370-0765.</p>
                )}

                <button type="submit" disabled={status === 'sending'}
                  className="w-full py-3.5 bg-[#00D9FF] hover:bg-[#00c5e8] text-[#0A1628] font-black text-[13px] uppercase tracking-widest rounded-lg transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,217,255,0.4)] hover:-translate-y-0.5 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
                  {status === 'sending' ? 'Sending...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceCTABlock;
